import { useMemo, useState } from 'react'
import { Alert, Button, Descriptions, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { formatDate } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { TWO_COLUMNS } from '@/shared/ui/descriptionColumns'
import { PageHeader } from '@/shared/ui/PageHeader'
import { StatTiles } from '@/shared/ui/StatTiles'
import { PurchaseFormModal } from '@/features/supplier/PurchaseFormModal'
import { PurchasesTable } from '@/features/supplier/PurchasesTable'
import { SupplierAttachmentsCard } from '@/features/supplier/SupplierAttachmentsCard'
import { SupplierFormModal } from '@/features/supplier/SupplierFormModal'
import {
  fetchSupplier,
  fetchSupplierLedger,
  fetchSupplierSummary,
  type SupplierSummary,
} from '@/features/supplier/supplierApi'

export function SupplierDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [editing, setEditing] = useState(false)
  const [creatingPurchase, setCreatingPurchase] = useState(false)

  const supplier = useQuery({
    queryKey: queryKeys.supplier.detail(id),
    queryFn: ({ signal }) => fetchSupplier(id, signal),
  })
  const summary = useQuery({
    queryKey: queryKeys.supplier.summary(id),
    queryFn: ({ signal }) => fetchSupplierSummary(id, signal),
  })
  const ledger = useQuery({
    queryKey: queryKeys.supplier.ledger(id),
    queryFn: ({ signal }) => fetchSupplierLedger(id, signal),
  })
  const purchaseFilter = useMemo(() => ({ supplierId: id, state: null, overdueOnly: false }), [id])
  const currency = summary.data?.totalPurchases.currency ?? user?.tenant.currency ?? ''

  if (supplier.isError) {
    return <Alert type="error" showIcon message={translateError(supplier.error, t)} />
  }
  const data = supplier.data

  return (
    <>
      <PageHeader
        title={data ? `${data.name} · ${data.code}` : t('common.loading')}
        description={data ? t(`supplier.category.${data.category}`) : undefined}
        extra={
          <Space>
            <Can permission="purchase.create">
              <Tooltip title={data && !data.active ? t('supplier.inactiveHint') : undefined}>
                <Button icon={<PlusOutlined />} disabled={!data?.active} onClick={() => setCreatingPurchase(true)}>
                  {t('purchase.action.create')}
                </Button>
              </Tooltip>
            </Can>
            <Can permission="supplier.manage">
              <Button icon={<EditOutlined />} disabled={!data} onClick={() => setEditing(true)}>
                {t('supplier.action.edit')}
              </Button>
            </Can>
          </Space>
        }
      />

      {summary.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(summary.error, t)} />
      ) : null}
      {summary.data ? <SummaryCards summary={summary.data} /> : null}

      <Tabs
        items={[
          {
            key: 'purchases',
            label: t('supplier.tab.purchases'),
            children: <PurchasesTable filter={purchaseFilter} tableId="supplier-purchases" hideSupplier />,
          },
          {
            key: 'ledger',
            label: t('supplier.tab.ledger'),
            children: (
              <Table
                size="small"
                rowKey={(row) => `${row.type}-${row.paymentId ?? row.purchaseId}`}
                loading={ledger.isPending}
                pagination={false}
                locale={{ emptyText: ledger.isError ? translateError(ledger.error, t) : t('supplier.ledger.empty') }}
                dataSource={ledger.data ?? []}
                columns={[
                  { key: 'date', title: t('supplier.ledger.date'), render: (_, row) => formatDate(row.date) },
                  {
                    key: 'type',
                    title: t('supplier.ledger.type'),
                    render: (_, row) => (
                      <Tag color={row.type === 'PURCHASE' ? 'blue' : 'green'}>{t(`supplier.ledger.kind.${row.type}`)}</Tag>
                    ),
                  },
                  { key: 'number', title: t('purchase.field.number'), render: (_, row) => row.purchaseNumber },
                  { key: 'reference', title: t('supplier.ledger.reference'), render: (_, row) => row.reference ?? t('common.dash') },
                  {
                    key: 'purchase',
                    title: t('supplier.ledger.purchase'),
                    align: 'right',
                    render: (_, row) => (row.purchaseMinor ? formatMoneyValue(money(row.purchaseMinor, currency)) : ''),
                  },
                  {
                    key: 'payment',
                    title: t('supplier.ledger.payment'),
                    align: 'right',
                    render: (_, row) => (row.paymentMinor ? formatMoneyValue(money(row.paymentMinor, currency)) : ''),
                  },
                  {
                    key: 'balance',
                    title: t('supplier.ledger.balance'),
                    align: 'right',
                    render: (_, row) => <Typography.Text strong>{formatMoneyValue(money(row.balanceMinor, currency))}</Typography.Text>,
                  },
                ]}
              />
            ),
          },
          {
            key: 'profile',
            label: t('supplier.tab.profile'),
            children: data ? (
              <Descriptions size="small" column={TWO_COLUMNS} bordered>
                <Descriptions.Item label={t('supplier.field.contactPerson')}>{data.contactPerson ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.phone')}>{data.phone ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.whatsapp')}>{data.whatsapp ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.email')}>{data.email ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.address')} span={2}>{data.address ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.taxId')}>{data.taxId ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.contractRef')}>{data.contractRef ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.bankDetails')} span={2}>{data.bankDetails ?? t('common.dash')}</Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.paymentTermsDays')}>
                  {t('supplier.termsValue', { count: data.paymentTermsDays })}
                </Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.creditLimit')}>
                  {data.creditLimitMinor == null ? t('common.dash') : formatMoneyValue(money(data.creditLimitMinor, currency))}
                </Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.active')}>
                  {data.active ? t('supplier.state.active') : t('supplier.state.inactive')}
                </Descriptions.Item>
                <Descriptions.Item label={t('supplier.field.notes')}>{data.notes ?? t('common.dash')}</Descriptions.Item>
              </Descriptions>
            ) : null,
          },
          { key: 'attachments', label: t('supplier.tab.attachments'), children: <SupplierAttachmentsCard supplierId={id} /> },
        ]}
      />

      <SupplierFormModal open={editing} supplier={data ?? null} onClose={() => setEditing(false)} onSaved={() => undefined} />
      <PurchaseFormModal
        open={creatingPurchase}
        purchase={null}
        presetSupplierId={id}
        onClose={() => setCreatingPurchase(false)}
        onSaved={() => undefined}
      />
    </>
  )
}

function SummaryCards({ summary }: { summary: SupplierSummary }) {
  const { t } = useTranslation()
  const creditUsed =
    summary.creditLimit !== null && summary.creditLimit.amount > 0 && summary.outstanding.amount > summary.creditLimit.amount
  return (
    <>
      <StatTiles
        tiles={[
          { key: 'purchases', title: t('supplier.summary.totalPurchases'), value: formatMoneyValue(summary.totalPurchases) },
          { key: 'paid', title: t('supplier.summary.totalPaid'), value: formatMoneyValue(summary.totalPaid) },
          {
            key: 'outstanding',
            title: t('supplier.summary.outstanding'),
            value: formatMoneyValue(summary.outstanding),
            extra: creditUsed ? <Typography.Text type="warning">{t('supplier.summary.overCredit')}</Typography.Text> : null,
          },
          {
            key: 'overdue',
            title: t('supplier.summary.overdue'),
            value: formatMoneyValue(summary.overdue),
            extra: summary.overdue.amount > 0 ? <Tag color="red">{t('purchase.paymentStatus.OVERDUE')}</Tag> : null,
          },
          { key: 'lastPurchase', title: t('supplier.summary.lastPurchase'), value: formatDate(summary.lastPurchaseOn) },
          { key: 'lastPayment', title: t('supplier.summary.lastPayment'), value: formatDate(summary.lastPaymentOn) },
        ]}
      />
      {summary.awaitingVerification > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('supplier.summary.awaiting', { count: summary.awaitingVerification })}
        />
      ) : null}
    </>
  )
}
